'use client';

import React, { useState } from "react";
import { useTranslations } from "next-intl";
import Button from "../components/Button";
import TradeModal from "../components/TradeModal";
import type { BuyDraftPayload, ExchangeDraftPayload } from "../features/trade/types";
import BuyModal from "../components/BuyModal";
import SellModal from "../components/SellModal";
import ExchangeModal from "../components/ExchangeModal";
import type { SellPayload } from "../services/sellService";
import ProductCard from "../components/ProductCard";
import NewModal from "../components/NewModal";
import MarketStatCards from "../components/LiveFeed";
import { authStorage } from "../lib/authStorage";
import { InfoSection } from "../features/dashboard/components/InfoSection";
import { useDashboardProducts } from "../features/dashboard/useDashboardProducts";
import Loader from "../components/Loader";
import type { ApiProduct } from "../types/domain";

const heroGradient =
  "linear-gradient(323deg,rgba(255,255,255,0) 0%, rgba(255,255,255,0) 54%, rgba(251,191,36,0.59) 100%)";

export default function Dashboard() {
  const t = useTranslations();
  const { products, isLoading } = useDashboardProducts();
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [tradeOpen, setTradeOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState<"buy" | "sell" | "exchange">("buy");
  const [selectedProduct, setSelectedProduct] = useState<ApiProduct | null>(null);
  const [nextModal, setNextModal] = useState<"buy" | "sell" | "exchange" | null>(null);
  const [buyDraft, setBuyDraft] = useState<BuyDraftPayload | null>(null);
  const [exchangeDraft, setExchangeDraft] = useState<ExchangeDraftPayload | null>(null);
  const [sellPayload, setSellPayload] = useState<SellPayload | null>(null);

  const handleOpenModal = (
    action: "buy" | "sell" | "exchange",
    product?: ApiProduct | { id: string; name: string }
  ) => {
    setCurrentAction(action);
    if (product) {
      setSelectedProduct(product as ApiProduct);
    }

    if (authStorage.getUserId()) {
      setTradeOpen(true);
      return;
    }

    setVerifyOpen(true);
  };

  const handleTradeSuccess = (
    action: "buy" | "sell" | "exchange",
    payload?: ExchangeDraftPayload | BuyDraftPayload | SellPayload
  ) => {
    setTradeOpen(false);
    setSelectedProduct(null);
    setNextModal(action);
    if (action === "buy") setBuyDraft((payload as BuyDraftPayload) ?? null);
    else if (action === "sell") setSellPayload((payload as SellPayload) ?? null);
    else setExchangeDraft((payload as ExchangeDraftPayload) ?? null);
  };

  const handleSampleBack = () => {
    setNextModal(null);
    setTradeOpen(true);
  };

  return (
    <div className="h-full mt-20 flex flex-col items-center gap-12 w-[90%] mx-auto">
      <MarketStatCards />

      <div
        className="w-full rounded-2xl shadow-2xl/30 p-4 sm:p-6 lg:p-8 flex flex-col lg:flex-row lg:gap-8 mb-12"
        style={{ background: heroGradient }}
      >
        <div className="w-full lg:w-1/2 flex flex-col items-center justify-center rounded-xl p-4">
          <img src="/logo/circular_logo.svg" alt="Agar Mart Large Logo" className="h-48 sm:h-60 lg:h-72" />
          <img src="/logo/text_logo.svg" alt="Agar Mart" className="w-full h-20 object-contain mt-2" />
        </div>
        <div className="w-full lg:w-1/2 flex flex-col justify-center gap-4 text-center lg:text-right">
          <div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-semibold text-[#0A2540] mb-2">
              {t('dashboard.tradeWithUs')}
            </h2>
            <p className="text-[#334155] text-sm sm:text-lg mb-4">
              {t('dashboard.heroDescription')}
            </p>
          </div>
          <div className="w-full sm:w-2/3 mx-auto lg:ml-auto lg:mr-0 flex flex-col items-stretch sm:items-end gap-2">
            <Button label={t('dashboard.buy')} color="blue" icon="/logo/buy_logo.svg" className="rounded-xl" onClick={() => handleOpenModal('buy')} />
            <Button label={t('dashboard.sell')} color="pink" icon="/logo/sell_logo.svg" className="rounded-xl" onClick={() => handleOpenModal('sell')} />
            <Button label={t('dashboard.exchange')} color="yellow" icon="/logo/exchange_logo.svg" className="rounded-xl" onClick={() => handleOpenModal('exchange')} />
          </div>
        </div>
      </div>

      <div id="product-catalog" className="w-full mb-8">
        <h2 className="text-3xl md:text-5xl font-bold text-[#0A2540] mb-1">
          {t('dashboard.productCatalog')}
        </h2>
        <p className="text-[#64748B] text-base md:text-xl mb-6">
          {t('dashboard.catalogDescription')}
        </p>
        <div className="flex gap-4 flex-nowrap overflow-x-auto items-stretch justify-start pt-4 pb-8 px-2 min-h-[200px]">
          {isLoading ? (
            <div className="w-full flex flex-col items-center justify-center py-12">
              <Loader />
            </div>
          ) : products.length === 0 ? (
            <div className="w-full flex flex-col items-center justify-center py-12">
              <p className="text-lg text-slate-500 font-semibold">No products available at the moment.</p>
            </div>
          ) : (
            products.map((product) => {
              const allowedActions: ("buy" | "sell" | "exchange")[] = [];
              if (product.canBuy) allowedActions.push("buy");
              if (product.canSell) allowedActions.push("sell");
              if (product.canExchange) allowedActions.push("exchange");

              return (
                <ProductCard
                  key={product.id}
                  productId={product.id}
                  title={product.name}
                  desc={product.description}
                  img={product.imageUrl}
                  price={product.buyPrice}
                  productObj={{ id: product.id, name: product.name }}
                  offer={product.offer === true}
                  offerMessage={product.offerMessage}
                  allowedActions={allowedActions}
                  onBuy={(item) => handleOpenModal("buy", item)}
                  onSell={(item) => handleOpenModal("sell", item)}
                  onExchange={(item) => handleOpenModal("exchange", item)}
                />
              );
            })
          )}
        </div>
      </div>

      <InfoSection
        id="about-us-section"
        heading={t('dashboard.aboutUs.heading')}
        subtitle={t('dashboard.aboutUs.subtitle')}
        paragraphs={[t('dashboard.aboutUs.p1'), t('dashboard.aboutUs.p2'), t('dashboard.aboutUs.p3')]}
        accentClassName="text-amber-600 font-bold"
        dividerClassName="bg-amber-400"
        gradient="linear-gradient(118deg,rgba(252, 236, 157, 1) 0%, rgba(255, 245, 217, 0.35) 52%, rgba(255, 255, 255, 0) 100%)"
        imageSrc="/logo/agarbatti_image.png"
        reverseDesktop
      />

      <InfoSection
        id="our-vision-section"
        heading={t('dashboard.ourVision.heading')}
        subtitle={t('dashboard.ourVision.subtitle')}
        paragraphs={[t('dashboard.ourVision.p1'), t('dashboard.ourVision.p2'), t('dashboard.ourVision.p3')]}
        accentClassName="text-blue-600"
        dividerClassName="bg-blue-400"
        gradient="linear-gradient(231deg,rgba(227, 242, 253, 1) 0%, rgba(227, 242, 253, 0.35) 52%, rgba(255, 255, 255, 0) 100%)"
        imageSrc="/logo/our_vision.png"
        imageAlt="Our Vision"
      />

      <InfoSection
        id="our-mission-section"
        heading={t('dashboard.ourMission.heading')}
        subtitle={t('dashboard.ourMission.subtitle')}
        paragraphs={[t('dashboard.ourMission.p1'), t('dashboard.ourMission.p2'), t('dashboard.ourMission.p3')]}
        accentClassName="text-emerald-600"
        dividerClassName="bg-emerald-400"
        gradient="linear-gradient(118deg,rgba(201, 255, 234, 1) 0%, rgba(236, 253, 245, 0.35) 52%, rgba(255, 255, 255, 0) 100%)"
        imageSrc="/logo/our_mission.png"
        imageAlt="Our Mission"
        reverseDesktop
      />

      <div id="contact-section" className="w-full mb-8" />

      <NewModal
        isOpen={verifyOpen}
        onClose={() => setVerifyOpen(false)}
        onSuccess={() => {
          setVerifyOpen(false);
          setTradeOpen(true);
        }}
      />

      <TradeModal
        isOpen={tradeOpen}
        onClose={() => {
          setTradeOpen(false);
          setSelectedProduct(null);
        }}
        onSuccess={handleTradeSuccess}
        variant={currentAction}
        selectedProduct={selectedProduct}
      />

      <BuyModal
        isOpen={nextModal === "buy"}
        onClose={() => { setNextModal(null); setBuyDraft(null); }}
        buyDraft={buyDraft}
        onBack={handleSampleBack}
      />
      <SellModal
        isOpen={nextModal === "sell"}
        onClose={() => { setNextModal(null); setSellPayload(null); }}
        sellPayload={sellPayload ?? undefined}
        onBack={handleSampleBack}
      />
      <ExchangeModal
        isOpen={nextModal === "exchange"}
        onClose={() => { setNextModal(null); setExchangeDraft(null); }}
        exchangeDraft={exchangeDraft}
        onBack={handleSampleBack}
      />
    </div>
  );
}
